/**
 * @module CreateNotificationPreferences
 * @package @geenius/notifications-solidjs-css
 * @description Wraps preference mutation flows for the SolidJS vanilla CSS
 * notifications variant.
 */

import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import type { Accessor } from 'solid-js'
import { createMemo } from 'solid-js'

/**
 * Async preference update function consumed by the primitive.
 */
export type UpdateNotificationPreferences = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => Promise<void>

/**
 * State returned by {@link createNotificationPreferences}.
 */
export interface CreateNotificationPreferencesResult {
  preferences: Accessor<NotificationPreferences | null | undefined>
  isLoading: Accessor<boolean>
  toggleChannel: (channel: NotificationChannel, enabled: boolean) => Promise<void>
  toggleType: (type: NotificationType, enabled: boolean) => Promise<void>
  setQuietHours: (start: string, end: string) => Promise<void>
}

/**
 * Manages async preference updates for the SolidJS CSS notifications package.
 *
 * @param preferences Preference accessor or loading state.
 * @param updatePreferences Optional async update function.
 * @returns Preference state plus helpers for toggles and quiet hours.
 */
export function createNotificationPreferences(
  preferences: Accessor<NotificationPreferences | null | undefined>,
  updatePreferences?: UpdateNotificationPreferences,
): CreateNotificationPreferencesResult {
  const isLoading = createMemo(() => preferences() === undefined)

  const toggleChannel = async (
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<void> => {
    const currentPreferences = preferences()

    if (!currentPreferences || !updatePreferences) {
      return
    }

    await updatePreferences({
      ...currentPreferences.channels,
      [channel]: enabled,
    })
  }

  const toggleType = async (
    type: NotificationType,
    enabled: boolean,
  ): Promise<void> => {
    const currentPreferences = preferences()

    if (!currentPreferences || !updatePreferences) {
      return
    }

    await updatePreferences(undefined, {
      ...currentPreferences.types,
      [type]: enabled,
    })
  }

  const setQuietHours = async (start: string, end: string): Promise<void> => {
    if (!updatePreferences) {
      return
    }

    await updatePreferences(undefined, undefined, { start, end })
  }

  return { preferences, isLoading, toggleChannel, toggleType, setQuietHours }
}
